import { Injectable, inject } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { QuestionService } from '_services/question.service';
import { defaultTestValue, shouldSkipTestFill } from '_services/dev-form-fill.defaults';

/**
 * Dev-only form autofill (Ctrl/Cmd+Shift+D).
 * Kept out of QuestionService so production question/state logic stays focused.
 */
@Injectable()
export class DevFormFillService {
  private qs = inject(QuestionService);

  /** Notify open section forms to rebuild after a bulk fill. */
  readonly formRefresh$ = new Subject<void>();

  constructor() {
    if (environment.production) {
      return;
    }
    fromEvent<KeyboardEvent>(window, 'keydown')
      .pipe(
        filter(event =>
          (event.ctrlKey || event.metaKey)
          && event.shiftKey
          && event.key.toLowerCase() === 'd'
        ),
      )
      .subscribe(event => {
        event.preventDefault();
        this.fillDefaults();
      });
  }

  fillDefaults(): boolean {
    if (environment.production) {
      return false;
    }
    if (!this.qs.isLoaded()) {
      console.warn('[dev] Questions not loaded yet — cannot fill defaults.');
      return false;
    }

    // Default only an unresolved gateway so existing dependency answers survive.
    this.qs.ensureDependentDefault();
    this.qs.updateQuestionStates();

    for (let pass = 0; pass < 3; pass++) {
      for (const q of this.qs.questions) {
        if (shouldSkipTestFill(q) || String(q.value ?? '').trim() !== '') {
          continue;
        }
        this.qs.setAnswer(q.id, defaultTestValue(q, this.qs.getAllowedChoiceValues(q.id)));
      }
      this.qs.updateQuestionStates();
    }

    this.formRefresh$.next();
    console.info('[dev] Filled unanswered questions with test defaults (Ctrl/Cmd+Shift+D).');
    return true;
  }
}
